import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Rocket, 
  Code, 
  Smartphone, 
  Cloud, 
  TrendingUp, 
  Users,
  Award,
  ArrowRight,
  CheckCircle,
  Star,
  Heart,
  DollarSign,
  ShoppingCart,
  BookOpen,
  Factory,
  Store,
  Stethoscope,
  BarChart3
} from 'lucide-react';

// Modern components
import { 
  Container, 
  Section, 
  SectionHeader, 
  Hero,
  TwoColumnLayout 
} from './modern/Layout';
import { 
  FeatureCard, 
  ServiceCard, 
  StatCard,
  TestimonialCard 
} from './modern/ModernCard';
import { ModernButton, ButtonGroup } from './modern/ModernButton';
import { 
  FadeIn, 
  SlideInLeft, 
  SlideInRight,
  StaggerContainer,
  StaggerItem 
} from './animations/MotionComponents';
import { ImageWithLoader } from './modern/ImageComponents';

// Default images - no API calls
const defaultServiceImages = {
  'web development': 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&h=400&fit=crop',
  'mobile app': 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&h=400&fit=crop',
  'cloud computing': 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
  'digital marketing': 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop'
};

const defaultHeroImage = 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1400&h=600&fit=crop';

const ModernHome = () => {
  const [loading, setLoading] = useState(false);
  const [heroImage] = useState(defaultHeroImage);
  const [serviceImages] = useState(defaultServiceImages);

  // No loading needed - use defaults immediately
  useEffect(() => {
    setLoading(false);
  }, []);

  const services = [
    {
      icon: <Code />,
      title: 'Web Development',
      description: 'Build responsive, scalable, and high-performance web applications with modern frameworks and best practices.',
      features: ['React & Next.js', 'Node.js & Spring Boot', 'RESTful APIs', 'Cloud Deployment'],
      image: serviceImages['web development']
    },
    {
      icon: <Smartphone />,
      title: 'Mobile App Development',
      description: 'Create native and cross-platform mobile applications that deliver exceptional user experiences.',
      features: ['iOS & Android', 'React Native', 'Flutter', 'App Store Optimization'],
      image: serviceImages['mobile app']
    },
    {
      icon: <Cloud />,
      title: 'Cloud Solutions',
      description: 'Migrate to the cloud and leverage scalable infrastructure for your business growth.',
      features: ['AWS & Azure', 'Kubernetes', 'CI/CD Pipelines', 'DevOps'],
      image: serviceImages['cloud computing']
    },
    {
      icon: <TrendingUp />,
      title: 'Digital Marketing',
      description: 'Amplify your online presence with data-driven marketing strategies and SEO optimization.',
      features: ['SEO & SEM', 'Social Media', 'Content Strategy', 'Analytics'],
      image: serviceImages['digital marketing']
    }
  ];

  const stats = [
    { icon: <Users />, value: '500+', label: 'Happy Clients', trend: '+25%', trendUp: true },
    { icon: <Award />, value: '1000+', label: 'Projects Completed', trend: '+30%', trendUp: true },
    { icon: <Star />, value: '15+', label: 'Years Experience', trend: 'Growing', trendUp: true },
    { icon: <CheckCircle />, value: '98%', label: 'Success Rate', trend: '+5%', trendUp: true }
  ];

  const testimonials = [
    {
      name: 'John Smith',
      role: 'CEO',
      company: 'TechCorp',
      quote: 'NehaInfotech delivered exceptional results. Their expertise in cloud migration saved us 40% in infrastructure costs.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      role: 'Product Manager',
      company: 'InnovateLabs',
      quote: 'The mobile app they developed exceeded our expectations. The user engagement increased by 60% in the first month.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'CTO',
      company: 'DataFlow Inc',
      quote: 'Professional, reliable, and innovative. They transformed our legacy system into a modern, scalable platform.',
      rating: 5
    }
  ];

  const industries = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Healthcare',
      description: 'HIPAA-compliant digital health solutions, patient management systems, and telemedicine platforms',
      color: 'from-red-500 to-pink-500',
      stats: '50+ deployments'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Financial Services',
      description: 'Secure banking solutions, fintech platforms, and blockchain-based payment systems',
      color: 'from-blue-500 to-cyan-500',
      stats: '30+ institutions'
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: 'E-Commerce',
      description: 'Scalable online stores, marketplace platforms, and omnichannel retail solutions',
      color: 'from-purple-500 to-pink-500',
      stats: '$2B+ transactions'
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Education',
      description: 'Learning management systems, virtual classrooms, and student engagement platforms',
      color: 'from-yellow-500 to-orange-500',
      stats: '100K+ students'
    },
    {
      icon: <Factory className="w-8 h-8" />,
      title: 'Manufacturing',
      description: 'IoT solutions, supply chain optimization, and production management systems',
      color: 'from-gray-600 to-slate-500',
      stats: '25+ factories'
    },
    {
      icon: <Store className="w-8 h-8" />,
      title: 'Retail',
      description: 'POS systems, inventory management, and customer loyalty programs',
      color: 'from-green-500 to-emerald-500',
      stats: '500+ stores'
    },
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: 'Pharma & Life Sciences',
      description: 'Clinical trial management, regulatory compliance, and lab automation',
      color: 'from-indigo-500 to-purple-500',
      stats: '15+ companies'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Enterprise & Analytics',
      description: 'Business intelligence, data warehousing, and predictive analytics solutions',
      color: 'from-teal-500 to-cyan-500',
      stats: '100+ dashboards'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <Hero
        title={
          <>
            Transform Your Business with{' '}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              Innovative Technology
            </span>
          </>
        }
        subtitle="Welcome to NehaInfotech"
        description="Transform your business with cutting-edge technology solutions and expert team guidance"
        image={heroImage || '/images/hero-tech.jpg'}
        buttons={
          <ButtonGroup>
            <ModernButton 
              variant="primary" 
              size="lg"
              icon={<Rocket />}
              onClick={() => window.location.href = '/contact'}
            >
              Get Started
            </ModernButton>
            <ModernButton 
              variant="outline" 
              size="lg"
              icon={<ArrowRight />}
              onClick={() => window.location.href = '/services'}
            >
              Our Services
            </ModernButton>
          </ButtonGroup>
        }
      />

      {/* Stats Section */}
      <Section background="gradient" padding="normal">
        <Container>
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <StatCard {...stat} />
                </motion.div>
              ))}
            </div>
          </FadeIn>
        </Container>
      </Section>

      {/* Services Section */}
      <Section background="white" padding="large">
        <Container>
          <SectionHeader
            subtitle="What We Offer"
            title="Our Core Services"
            description="Comprehensive technology solutions tailored to your business needs"
          />

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <StaggerItem key={index}>
                <ServiceCard {...service} />
              </StaggerItem>
            ))}
          </StaggerContainer>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <ModernButton variant="primary" size="lg" icon={<ArrowRight />}>
                View All Services
              </ModernButton>
            </Link>
          </motion.div>
        </Container>
      </Section>

      {/* Why Choose Us Section */}
      <Section background="gray" padding="large">
        <Container>
          <TwoColumnLayout
            left={
              <div className="space-y-6">
                <SectionHeader
                  subtitle="Why Choose Us"
                  title="Excellence in Every Project"
                  description="We combine technical expertise with business acumen to deliver solutions that drive real results."
                  align="left"
                />
                
                <div className="space-y-4">
                  {[
                    'Expert team with 15+ years experience',
                    'Agile development methodology',
                    '24/7 support and maintenance',
                    'Competitive pricing with quality assurance',
                    'On-time delivery guarantee'
                  ].map((item, index) => (
                    <SlideInLeft key={index} delay={index * 0.1}>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="text-green-500 flex-shrink-0 mt-1" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    </SlideInLeft>
                  ))}
                </div>

                <ButtonGroup>
                  <Link to="/about">
                    <ModernButton variant="primary">Learn More</ModernButton>
                  </Link>
                  <Link to="/contact">
                    <ModernButton variant="outline">Contact Us</ModernButton>
                  </Link>
                </ButtonGroup>
              </div>
            }
            right={
              <SlideInRight>
                <div className="relative">
                  <ImageWithLoader
                    src={serviceImages['cloud computing'] || '/images/team-work.jpg'}
                    alt="Team collaboration"
                    className="rounded-2xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-gradient-primary text-white p-6 rounded-xl shadow-glow-lg">
                    <div className="text-3xl font-bold">15+</div>
                    <div className="text-sm">Years of Excellence</div>
                  </div>
                </div>
              </SlideInRight>
            }
          />
        </Container>
      </Section>

      {/* Industries We Are Transforming Section */}
      <Section background="white" padding="large">
        <Container>
          <SectionHeader
            subtitle="Industry Expertise"
            title="Industries We Are Transforming"
            description="Empowering businesses across diverse sectors with innovative technology solutions"
          />

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                  transition={{ duration: 0.3 }}
                  className="group h-full"
                >
                  <div className="relative h-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors p-6 space-y-4">
                    {/* Background Gradient Accent */}
                    <div className={`absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br ${industry.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`} />
                    
                    {/* Icon with Gradient Background */}
                    <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${industry.color} flex items-center justify-center text-white shadow-lg transform group-hover:scale-110 transition-transform`}>
                      {industry.icon}
                    </div>

                    {/* Content */}
                    <div className="relative space-y-3">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {industry.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        {industry.description}
                      </p>
                      
                      {/* Stats Badge */}
                      <div className="flex items-center gap-2 pt-2">
                        <div className={`h-1 w-1 rounded-full bg-gradient-to-r ${industry.color}`} />
                        <span className="text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r inline-block" style={{backgroundImage: `linear-gradient(to right, var(--tw-gradient-stops))`}}>
                          {industry.stats}
                        </span>
                      </div>
                    </div>

                    {/* Bottom Border Accent */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${industry.color} transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300`} />
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* CTA for Industries */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
              Don't see your industry? We work with any sector. Let's discuss your specific needs.
            </p>
            <Link to="/contact">
              <ModernButton variant="outline" size="lg" icon={<ArrowRight />}>
                Explore Your Industry Solutions
              </ModernButton>
            </Link>
          </motion.div>
        </Container>
      </Section>

      {/* Testimonials Section */}
      <Section background="white" padding="large">
        <Container>
          <SectionHeader
            subtitle="Client Success Stories"
            title="What Our Clients Say"
            description="Don't just take our word for it - hear from our satisfied clients"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <TestimonialCard {...testimonial} />
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="gradient" padding="large">
        <Container>
          <FadeIn>
            <div className="text-center space-y-8 max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300">
                Let's discuss how we can help you achieve your technology goals
              </p>
              <ButtonGroup className="justify-center">
                <Link to="/contact">
                  <ModernButton variant="primary" size="xl" icon={<Rocket />}>
                    Start Your Project
                  </ModernButton>
                </Link>
                <Link to="/portfolio">
                  <ModernButton variant="outline" size="xl">
                    View Portfolio
                  </ModernButton>
                </Link>
              </ButtonGroup>
            </div>
          </FadeIn>
        </Container>
      </Section>
    </div>
  );
};

export default ModernHome;
